import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useTheme } from "../../lib/ThemeContext";
import { getThemeColors } from "./themeColors";

/*
 ╔═══════════════════════════════════════════════════════════════════╗
 ║  Sentence Studio v4 — Single-Line + Collapsible Regions          ║
 ║                                                                   ║
 ║  Key design decisions:                                            ║
 ║                                                                   ║
 ║  1. ONE LINE — sentence never wraps. Container scrolls            ║
 ║     horizontally. Eliminates all cross-line rendering bugs.       ║
 ║                                                                   ║
 ║  2. COLLAPSIBLE — parenthetical structures (appositives,          ║
 ║     relative clauses, dependent clauses) can be collapsed to      ║
 ║     show the sentence skeleton. Different iconography per type:   ║
 ║                                                                   ║
 ║     Appositives:        [...] (removable insert)                  ║
 ║     Relative clauses:   ⟨...⟩ (embedded modifier)                ║
 ║     Dependent clauses:  ⟨...⟩ (subordinate context)              ║
 ║                                                                   ║
 ║  3. SINGLE LAYER — one annotation layer visible at a time.        ║
 ║                                                                   ║
 ║  Zone rules (single text line):                                   ║
 ║    ABOVE:  curved arcs (arrows: subject→verb, modifier→target)    ║
 ║    INLINE: highlights (POS labels with background tint)           ║
 ║    BELOW:  underlines (clauses & phrases), braces (appositives)   ║
 ╚═══════════════════════════════════════════════════════════════════╝
*/

// ─── LAYER DEFINITIONS ───────────────────────────────────────

const LAYERS = {
  clauses:      { name: "Clauses",      icon: "C", desc: "Independent & dependent clauses" },
  phrases:      { name: "Phrases",      icon: "P", desc: "NP, VP, PP and their functions" },
  appositives:  { name: "Appositives",  icon: "A", desc: "Parentheticals & appositives" },
  "subject-verb": { name: "Subj → Verb", icon: "→", desc: "Subject-verb-object arrows" },
  pos:          { name: "Parts of Speech", icon: "◆", desc: "Noun, verb, adj, etc." },
};

// ─── SAMPLE DATA ──────────────────────────────────────────────

const SAMPLE = {
  sentence: "Rachel hit Wilson who, though a kind man under normal conditions, hit his own daughter.",
  tokens: [
    { id: 0,  text: "Rachel",     pos: "noun",        posDetail: "proper noun" },
    { id: 1,  text: "hit",        pos: "verb",        posDetail: "transitive verb (past tense)" },
    { id: 2,  text: "Wilson",     pos: "noun",        posDetail: "proper noun" },
    { id: 3,  text: "who",        pos: "pronoun",     posDetail: "relative pronoun" },
    { id: 4,  text: ",",          pos: "punctuation" },
    { id: 5,  text: "though",     pos: "conjunction", posDetail: "subordinating conjunction" },
    { id: 6,  text: "a",          pos: "determiner",  posDetail: "article" },
    { id: 7,  text: "kind",       pos: "adjective" },
    { id: 8,  text: "man",        pos: "noun",        posDetail: "common noun" },
    { id: 9,  text: "under",      pos: "preposition" },
    { id: 10, text: "normal",     pos: "adjective" },
    { id: 11, text: "conditions", pos: "noun",        posDetail: "common noun (plural)" },
    { id: 12, text: ",",          pos: "punctuation" },
    { id: 13, text: "hit",        pos: "verb",        posDetail: "transitive verb (past tense)" },
    { id: 14, text: "his",        pos: "determiner",  posDetail: "possessive determiner" },
    { id: 15, text: "own",        pos: "adjective" },
    { id: 16, text: "daughter",   pos: "noun",        posDetail: "common noun" },
    { id: 17, text: ".",          pos: "punctuation" },
  ],
  collapsible: [
    { id: "col-1", type: "appositive", start: 4, end: 12,
      label: "concessive appositive",
      summary: "though a kind man…" },
    { id: "col-2", type: "relative", start: 3, end: 16,
      label: "relative clause",
      summary: "who … hit his own daughter" },
  ],
  annotations: [
    // ── CLAUSES ──
    { id: "cl-1", layer: "clauses", type: "underline", label: "independent clause",
      start: 0, end: 2, color: "#3b82f6",
      description: "Main clause: Rachel (subject) + hit (verb) + Wilson (object)." },
    { id: "cl-2", layer: "clauses", type: "underline", label: "relative clause",
      start: 3, end: 17, color: "#a855f7",
      description: "Dependent clause modifying 'Wilson', introduced by the relative pronoun 'who'. Contains its own subject ('who'), verb ('hit'), and object ('his own daughter')." },
    { id: "cl-3", layer: "clauses", type: "arrow", label: "modifies",
      start: 3, end: 3, target: 2, color: "#a855f7",
      description: "The relative pronoun 'who' refers back to Wilson." },

    // ── APPOSITIVES ──
    { id: "ap-1", layer: "appositives", type: "brace", label: "concessive appositive",
      start: 4, end: 12, color: "#f59e0b",
      description: "A parenthetical aside set off by commas. It concedes that Wilson is normally kind before revealing what he did. Could be removed without breaking the sentence." },

    // ── PHRASES ──
    { id: "ph-1", layer: "phrases", type: "underline", label: "NP — subject",
      start: 0, end: 0, color: "#3b82f6",
      description: "'Rachel' is a noun phrase acting as the subject of the main clause." },
    { id: "ph-2", layer: "phrases", type: "underline", label: "NP — object",
      start: 2, end: 2, color: "#10b981",
      description: "'Wilson' is a noun phrase acting as the direct object of 'hit'." },
    { id: "ph-3", layer: "phrases", type: "underline", label: "NP",
      start: 6, end: 8, color: "#f59e0b",
      description: "'a kind man' — noun phrase within the appositive." },
    { id: "ph-4", layer: "phrases", type: "underline", label: "PP — modifier",
      start: 9, end: 11, color: "#06b6d4",
      description: "'under normal conditions' — prepositional phrase modifying 'man'." },
    { id: "ph-5", layer: "phrases", type: "arrow", label: "modifies",
      start: 9, end: 11, target: 8, color: "#06b6d4",
      description: "The prepositional phrase 'under normal conditions' tells us more about what kind of man Wilson is." },
    { id: "ph-6", layer: "phrases", type: "underline", label: "NP — object",
      start: 14, end: 16, color: "#10b981",
      description: "'his own daughter' — noun phrase, direct object of the second 'hit'." },

    // ── SUBJECT-VERB ──
    { id: "sv-1", layer: "subject-verb", type: "arrow", label: "subject → verb",
      start: 0, end: 0, target: 1, color: "#3b82f6",
      description: "Rachel (subject) performs the action 'hit'. Active voice." },
    { id: "sv-2", layer: "subject-verb", type: "arrow", label: "verb → object",
      start: 1, end: 1, target: 2, color: "#10b981",
      description: "'hit' acts upon Wilson (direct object)." },
    { id: "sv-3", layer: "subject-verb", type: "arrow", label: "subject → verb",
      start: 3, end: 3, target: 13, color: "#a855f7",
      description: "'who' (= Wilson) is the subject of the second 'hit'. Active voice." },
    { id: "sv-4", layer: "subject-verb", type: "arrow", label: "verb → object",
      start: 13, end: 13, targetSpan: [14, 16], color: "#10b981",
      description: "The second 'hit' acts upon 'his own daughter' (direct object)." },

    // ── POS ──
    { id: "p-0",  layer: "pos", type: "highlight", label: "noun",  start: 0,  end: 0,  color: "#3b82f6" },
    { id: "p-1",  layer: "pos", type: "highlight", label: "verb",  start: 1,  end: 1,  color: "#ef4444" },
    { id: "p-2",  layer: "pos", type: "highlight", label: "noun",  start: 2,  end: 2,  color: "#3b82f6" },
    { id: "p-3",  layer: "pos", type: "highlight", label: "pron.", start: 3,  end: 3,  color: "#a855f7" },
    { id: "p-5",  layer: "pos", type: "highlight", label: "conj.", start: 5,  end: 5,  color: "#eab308" },
    { id: "p-6",  layer: "pos", type: "highlight", label: "det.",  start: 6,  end: 6,  color: "#6b7280" },
    { id: "p-7",  layer: "pos", type: "highlight", label: "adj.",  start: 7,  end: 7,  color: "#f97316" },
    { id: "p-8",  layer: "pos", type: "highlight", label: "noun",  start: 8,  end: 8,  color: "#3b82f6" },
    { id: "p-9",  layer: "pos", type: "highlight", label: "prep.", start: 9,  end: 9,  color: "#06b6d4" },
    { id: "p-10", layer: "pos", type: "highlight", label: "adj.",  start: 10, end: 10, color: "#f97316" },
    { id: "p-11", layer: "pos", type: "highlight", label: "noun",  start: 11, end: 11, color: "#3b82f6" },
    { id: "p-13", layer: "pos", type: "highlight", label: "verb",  start: 13, end: 13, color: "#ef4444" },
    { id: "p-14", layer: "pos", type: "highlight", label: "det.",  start: 14, end: 14, color: "#6b7280" },
    { id: "p-15", layer: "pos", type: "highlight", label: "adj.",  start: 15, end: 15, color: "#f97316" },
    { id: "p-16", layer: "pos", type: "highlight", label: "noun",  start: 16, end: 16, color: "#3b82f6" },
  ],
};

// ─── SAMPLE DATA: PASSIVE VOICE ───────────────────────────────

const PASSIVE = {
  sentence: "The letter was written by Marie, who had been living abroad for years.",
  tokens: [
    { id: 0,  text: "The",      pos: "determiner",  posDetail: "article" },
    { id: 1,  text: "letter",   pos: "noun",         posDetail: "common noun" },
    { id: 2,  text: "was",      pos: "verb",         posDetail: "auxiliary verb (past)" },
    { id: 3,  text: "written",  pos: "verb",         posDetail: "past participle" },
    { id: 4,  text: "by",       pos: "preposition" },
    { id: 5,  text: "Marie",    pos: "noun",         posDetail: "proper noun" },
    { id: 6,  text: ",",        pos: "punctuation" },
    { id: 7,  text: "who",      pos: "pronoun",      posDetail: "relative pronoun" },
    { id: 8,  text: "had",      pos: "verb",         posDetail: "auxiliary verb (past)" },
    { id: 9,  text: "been",     pos: "verb",         posDetail: "auxiliary verb (past participle)" },
    { id: 10, text: "living",   pos: "verb",         posDetail: "present participle" },
    { id: 11, text: "abroad",   pos: "adverb" },
    { id: 12, text: "for",      pos: "preposition" },
    { id: 13, text: "years",    pos: "noun",         posDetail: "common noun (plural)" },
    { id: 14, text: ".",        pos: "punctuation" },
  ],
  collapsible: [
    { id: "col-1", type: "relative", start: 6, end: 13,
      label: "relative clause",
      summary: "who had been living abroad…" },
  ],
  annotations: [
    // ── CLAUSES ──
    { id: "cl-1", layer: "clauses", type: "underline", label: "independent clause (passive)",
      start: 0, end: 5, color: "#3b82f6",
      description: "Main clause in passive voice: 'The letter' (subject) + 'was written' (passive verb) + 'by Marie' (agent)." },
    { id: "cl-2", layer: "clauses", type: "underline", label: "relative clause",
      start: 7, end: 14, color: "#a855f7",
      description: "Dependent clause modifying 'Marie'. Uses past perfect continuous tense ('had been living')." },
    { id: "cl-3", layer: "clauses", type: "arrow", label: "modifies",
      start: 7, end: 7, target: 5, color: "#a855f7",
      description: "'who' refers back to Marie." },

    // ── PHRASES ──
    { id: "ph-1", layer: "phrases", type: "underline", label: "NP — subject",
      start: 0, end: 1, color: "#3b82f6",
      description: "'The letter' — noun phrase acting as the grammatical subject. In passive voice, the subject receives the action." },
    { id: "ph-2", layer: "phrases", type: "underline", label: "VP — passive",
      start: 2, end: 3, color: "#ef4444",
      description: "'was written' — verb phrase in passive voice. Auxiliary 'was' + past participle 'written'." },
    { id: "ph-3", layer: "phrases", type: "underline", label: "PP — agent",
      start: 4, end: 5, color: "#06b6d4",
      description: "'by Marie' — prepositional phrase identifying the agent." },
    { id: "ph-4", layer: "phrases", type: "underline", label: "VP — past perfect continuous",
      start: 8, end: 10, color: "#ef4444",
      description: "'had been living' — three-part verb phrase: past perfect continuous tense." },
    { id: "ph-5", layer: "phrases", type: "underline", label: "PP — duration",
      start: 12, end: 13, color: "#06b6d4",
      description: "'for years' — prepositional phrase indicating duration." },

    // ── SUBJECT-VERB ──
    { id: "sv-1", layer: "subject-verb", type: "arrow", label: "subject ← verb (passive)",
      start: 2, end: 3, target: 1, color: "#3b82f6",
      description: "Passive voice: the verb 'was written' acts upon the subject 'letter'." },
    { id: "sv-2", layer: "subject-verb", type: "arrow", label: "agent",
      start: 4, end: 5, target: 3, color: "#06b6d4",
      description: "'by Marie' reveals who actually performed the writing." },
    { id: "sv-3", layer: "subject-verb", type: "arrow", label: "subject → verb",
      start: 7, end: 7, target: 10, color: "#a855f7",
      description: "'who' (= Marie) is the subject of 'living'. Active voice in the relative clause." },

    // ── POS ──
    { id: "p-0",  layer: "pos", type: "highlight", label: "det.",  start: 0,  end: 0,  color: "#6b7280" },
    { id: "p-1",  layer: "pos", type: "highlight", label: "noun",  start: 1,  end: 1,  color: "#3b82f6" },
    { id: "p-2",  layer: "pos", type: "highlight", label: "aux.",  start: 2,  end: 2,  color: "#ef4444" },
    { id: "p-3",  layer: "pos", type: "highlight", label: "verb",  start: 3,  end: 3,  color: "#ef4444" },
    { id: "p-4",  layer: "pos", type: "highlight", label: "prep.", start: 4,  end: 4,  color: "#06b6d4" },
    { id: "p-5",  layer: "pos", type: "highlight", label: "noun",  start: 5,  end: 5,  color: "#3b82f6" },
    { id: "p-7",  layer: "pos", type: "highlight", label: "pron.", start: 7,  end: 7,  color: "#a855f7" },
    { id: "p-8",  layer: "pos", type: "highlight", label: "aux.",  start: 8,  end: 8,  color: "#ef4444" },
    { id: "p-9",  layer: "pos", type: "highlight", label: "aux.",  start: 9,  end: 9,  color: "#ef4444" },
    { id: "p-10", layer: "pos", type: "highlight", label: "verb",  start: 10, end: 10, color: "#ef4444" },
    { id: "p-11", layer: "pos", type: "highlight", label: "adv.",  start: 11, end: 11, color: "#f97316" },
    { id: "p-12", layer: "pos", type: "highlight", label: "prep.", start: 12, end: 12, color: "#06b6d4" },
    { id: "p-13", layer: "pos", type: "highlight", label: "noun",  start: 13, end: 13, color: "#3b82f6" },
  ],
};

// ─── SAMPLE DATA: COMPOUND-COMPLEX ───────────────────────────

const COMPOUND_COMPLEX = {
  sentence: "Although the storm had passed, the streets were still flooded, and the rescue teams worked through the night.",
  tokens: [
    { id: 0,  text: "Although",  pos: "conjunction",  posDetail: "subordinating conjunction" },
    { id: 1,  text: "the",       pos: "determiner",   posDetail: "article" },
    { id: 2,  text: "storm",     pos: "noun",         posDetail: "common noun" },
    { id: 3,  text: "had",       pos: "verb",         posDetail: "auxiliary verb (past)" },
    { id: 4,  text: "passed",    pos: "verb",         posDetail: "past participle" },
    { id: 5,  text: ",",         pos: "punctuation" },
    { id: 6,  text: "the",       pos: "determiner",   posDetail: "article" },
    { id: 7,  text: "streets",   pos: "noun",         posDetail: "common noun (plural)" },
    { id: 8,  text: "were",      pos: "verb",         posDetail: "auxiliary verb (past)" },
    { id: 9,  text: "still",     pos: "adverb" },
    { id: 10, text: "flooded",   pos: "verb",         posDetail: "past participle (passive)" },
    { id: 11, text: ",",         pos: "punctuation" },
    { id: 12, text: "and",       pos: "conjunction",  posDetail: "coordinating conjunction" },
    { id: 13, text: "the",       pos: "determiner",   posDetail: "article" },
    { id: 14, text: "rescue",    pos: "adjective",    posDetail: "attributive noun/adjective" },
    { id: 15, text: "teams",     pos: "noun",         posDetail: "common noun (plural)" },
    { id: 16, text: "worked",    pos: "verb",         posDetail: "intransitive verb (past)" },
    { id: 17, text: "through",   pos: "preposition" },
    { id: 18, text: "the",       pos: "determiner",   posDetail: "article" },
    { id: 19, text: "night",     pos: "noun",         posDetail: "common noun" },
    { id: 20, text: ".",         pos: "punctuation" },
  ],
  collapsible: [
    { id: "col-1", type: "dependent", start: 0, end: 5,
      label: "concessive clause",
      summary: "Although the storm had passed" },
  ],
  annotations: [
    // ── CLAUSES ──
    { id: "cl-1", layer: "clauses", type: "underline", label: "dependent clause (concessive)",
      start: 0, end: 5, color: "#f59e0b",
      description: "Subordinate clause beginning with 'Although'. Concedes that the storm passed before revealing the contrast: the streets were still flooded." },
    { id: "cl-2", layer: "clauses", type: "underline", label: "independent clause (passive)",
      start: 6, end: 10, color: "#3b82f6",
      description: "First independent clause: 'the streets were still flooded'. Passive voice — the streets didn't flood themselves." },
    { id: "cl-3", layer: "clauses", type: "underline", label: "independent clause",
      start: 12, end: 20, color: "#10b981",
      description: "Second independent clause joined by 'and': 'the rescue teams worked through the night'. Active voice." },

    // ── PHRASES ──
    { id: "ph-1", layer: "phrases", type: "underline", label: "NP — subject",
      start: 1, end: 2, color: "#3b82f6",
      description: "'the storm' — subject of the dependent clause." },
    { id: "ph-2", layer: "phrases", type: "underline", label: "VP — past perfect",
      start: 3, end: 4, color: "#ef4444",
      description: "'had passed' — past perfect tense, showing completion before the main clause's time." },
    { id: "ph-3", layer: "phrases", type: "underline", label: "NP — subject",
      start: 6, end: 7, color: "#3b82f6",
      description: "'the streets' — subject of the first independent clause." },
    { id: "ph-4", layer: "phrases", type: "underline", label: "VP — passive",
      start: 8, end: 10, color: "#ef4444",
      description: "'were still flooded' — passive voice verb phrase." },
    { id: "ph-5", layer: "phrases", type: "underline", label: "NP — subject",
      start: 13, end: 15, color: "#3b82f6",
      description: "'the rescue teams' — subject of the second independent clause." },
    { id: "ph-6", layer: "phrases", type: "underline", label: "PP — time",
      start: 17, end: 19, color: "#06b6d4",
      description: "'through the night' — prepositional phrase indicating duration." },

    // ── SUBJECT-VERB ──
    { id: "sv-1", layer: "subject-verb", type: "arrow", label: "subject → verb",
      start: 2, end: 2, target: 4, color: "#f59e0b",
      description: "'storm' (subject) performed the action 'passed'." },
    { id: "sv-2", layer: "subject-verb", type: "arrow", label: "subject ← verb (passive)",
      start: 8, end: 10, target: 7, color: "#3b82f6",
      description: "Passive: 'were still flooded' acts upon 'streets'." },
    { id: "sv-3", layer: "subject-verb", type: "arrow", label: "subject → verb",
      start: 15, end: 15, target: 16, color: "#10b981",
      description: "'teams' (subject) performed the action 'worked'." },

    // ── POS ──
    { id: "p-0",  layer: "pos", type: "highlight", label: "conj.", start: 0,  end: 0,  color: "#eab308" },
    { id: "p-1",  layer: "pos", type: "highlight", label: "det.",  start: 1,  end: 1,  color: "#6b7280" },
    { id: "p-2",  layer: "pos", type: "highlight", label: "noun",  start: 2,  end: 2,  color: "#3b82f6" },
    { id: "p-3",  layer: "pos", type: "highlight", label: "aux.",  start: 3,  end: 3,  color: "#ef4444" },
    { id: "p-4",  layer: "pos", type: "highlight", label: "verb",  start: 4,  end: 4,  color: "#ef4444" },
    { id: "p-6",  layer: "pos", type: "highlight", label: "det.",  start: 6,  end: 6,  color: "#6b7280" },
    { id: "p-7",  layer: "pos", type: "highlight", label: "noun",  start: 7,  end: 7,  color: "#3b82f6" },
    { id: "p-8",  layer: "pos", type: "highlight", label: "aux.",  start: 8,  end: 8,  color: "#ef4444" },
    { id: "p-9",  layer: "pos", type: "highlight", label: "adv.",  start: 9,  end: 9,  color: "#f97316" },
    { id: "p-10", layer: "pos", type: "highlight", label: "verb",  start: 10, end: 10, color: "#ef4444" },
    { id: "p-12", layer: "pos", type: "highlight", label: "conj.", start: 12, end: 12, color: "#eab308" },
    { id: "p-13", layer: "pos", type: "highlight", label: "det.",  start: 13, end: 13, color: "#6b7280" },
    { id: "p-14", layer: "pos", type: "highlight", label: "adj.",  start: 14, end: 14, color: "#f97316" },
    { id: "p-15", layer: "pos", type: "highlight", label: "noun",  start: 15, end: 15, color: "#3b82f6" },
    { id: "p-16", layer: "pos", type: "highlight", label: "verb",  start: 16, end: 16, color: "#ef4444" },
    { id: "p-17", layer: "pos", type: "highlight", label: "prep.", start: 17, end: 17, color: "#06b6d4" },
    { id: "p-18", layer: "pos", type: "highlight", label: "det.",  start: 18, end: 18, color: "#6b7280" },
    { id: "p-19", layer: "pos", type: "highlight", label: "noun",  start: 19, end: 19, color: "#3b82f6" },
  ],
};

// ─── SAMPLE DATA: APPOSITIVE ──────────────────────────────────

const APPOSITIVE = {
  sentence: "My neighbor, a retired surgeon with forty years of experience, saved the child's life.",
  tokens: [
    { id: 0,  text: "My",         pos: "determiner",  posDetail: "possessive determiner" },
    { id: 1,  text: "neighbor",   pos: "noun",        posDetail: "common noun" },
    { id: 2,  text: ",",          pos: "punctuation" },
    { id: 3,  text: "a",          pos: "determiner",  posDetail: "article" },
    { id: 4,  text: "retired",    pos: "adjective",   posDetail: "past participle as adjective" },
    { id: 5,  text: "surgeon",    pos: "noun",        posDetail: "common noun" },
    { id: 6,  text: "with",       pos: "preposition" },
    { id: 7,  text: "forty",      pos: "adjective",   posDetail: "numeral adjective" },
    { id: 8,  text: "years",      pos: "noun",        posDetail: "common noun (plural)" },
    { id: 9,  text: "of",         pos: "preposition" },
    { id: 10, text: "experience", pos: "noun",        posDetail: "uncountable noun" },
    { id: 11, text: ",",          pos: "punctuation" },
    { id: 12, text: "saved",      pos: "verb",        posDetail: "transitive verb (past)" },
    { id: 13, text: "the",        pos: "determiner",  posDetail: "article" },
    { id: 14, text: "child's",    pos: "noun",        posDetail: "possessive noun" },
    { id: 15, text: "life",       pos: "noun",        posDetail: "common noun" },
    { id: 16, text: ".",          pos: "punctuation" },
  ],
  collapsible: [
    { id: "col-1", type: "appositive", start: 2, end: 11,
      label: "renaming appositive",
      summary: "a retired surgeon…" },
  ],
  annotations: [
    // ── CLAUSES ──
    { id: "cl-1", layer: "clauses", type: "underline", label: "independent clause",
      start: 0, end: 16, color: "#3b82f6",
      description: "Single independent clause: 'My neighbor saved the child's life.' The appositive interrupts but doesn't create a separate clause." },

    // ── APPOSITIVES ──
    { id: "ap-1", layer: "appositives", type: "brace", label: "renaming appositive",
      start: 2, end: 11, color: "#f59e0b",
      description: "A renaming appositive that tells us who the neighbor is: 'a retired surgeon with forty years of experience'. Set off by commas, it renames 'my neighbor'." },
    { id: "ap-2", layer: "appositives", type: "arrow", label: "renames",
      start: 5, end: 5, target: 1, color: "#f59e0b",
      description: "The appositive 'surgeon' renames 'neighbor' — they refer to the same person." },

    // ── PHRASES ──
    { id: "ph-1", layer: "phrases", type: "underline", label: "NP — subject",
      start: 0, end: 1, color: "#3b82f6",
      description: "'My neighbor' — noun phrase acting as the subject." },
    { id: "ph-2", layer: "phrases", type: "underline", label: "NP — appositive core",
      start: 3, end: 5, color: "#f59e0b",
      description: "'a retired surgeon' — the core noun phrase within the appositive." },
    { id: "ph-3", layer: "phrases", type: "underline", label: "PP — modifier",
      start: 6, end: 10, color: "#06b6d4",
      description: "'with forty years of experience' — prepositional phrase modifying 'surgeon'." },
    { id: "ph-4", layer: "phrases", type: "arrow", label: "modifies",
      start: 6, end: 10, target: 5, color: "#06b6d4",
      description: "This prepositional phrase tells us more about the surgeon." },
    { id: "ph-5", layer: "phrases", type: "underline", label: "NP — object",
      start: 13, end: 15, color: "#10b981",
      description: "'the child's life' — noun phrase acting as the direct object of 'saved'." },

    // ── SUBJECT-VERB ──
    { id: "sv-1", layer: "subject-verb", type: "arrow", label: "subject → verb",
      start: 1, end: 1, target: 12, color: "#3b82f6",
      description: "'neighbor' (subject) performed the action 'saved'. The appositive separates them but doesn't change the relationship." },
    { id: "sv-2", layer: "subject-verb", type: "arrow", label: "verb → object",
      start: 12, end: 12, target: 15, color: "#10b981",
      description: "'saved' acts upon 'life' (direct object)." },

    // ── POS ──
    { id: "p-0",  layer: "pos", type: "highlight", label: "det.",  start: 0,  end: 0,  color: "#6b7280" },
    { id: "p-1",  layer: "pos", type: "highlight", label: "noun",  start: 1,  end: 1,  color: "#3b82f6" },
    { id: "p-3",  layer: "pos", type: "highlight", label: "det.",  start: 3,  end: 3,  color: "#6b7280" },
    { id: "p-4",  layer: "pos", type: "highlight", label: "adj.",  start: 4,  end: 4,  color: "#f97316" },
    { id: "p-5",  layer: "pos", type: "highlight", label: "noun",  start: 5,  end: 5,  color: "#3b82f6" },
    { id: "p-6",  layer: "pos", type: "highlight", label: "prep.", start: 6,  end: 6,  color: "#06b6d4" },
    { id: "p-7",  layer: "pos", type: "highlight", label: "adj.",  start: 7,  end: 7,  color: "#f97316" },
    { id: "p-8",  layer: "pos", type: "highlight", label: "noun",  start: 8,  end: 8,  color: "#3b82f6" },
    { id: "p-9",  layer: "pos", type: "highlight", label: "prep.", start: 9,  end: 9,  color: "#06b6d4" },
    { id: "p-10", layer: "pos", type: "highlight", label: "noun",  start: 10, end: 10, color: "#3b82f6" },
    { id: "p-12", layer: "pos", type: "highlight", label: "verb",  start: 12, end: 12, color: "#ef4444" },
    { id: "p-13", layer: "pos", type: "highlight", label: "det.",  start: 13, end: 13, color: "#6b7280" },
    { id: "p-14", layer: "pos", type: "highlight", label: "noun",  start: 14, end: 14, color: "#3b82f6" },
    { id: "p-15", layer: "pos", type: "highlight", label: "noun",  start: 15, end: 15, color: "#3b82f6" },
  ],
};

// ─── DATASET LOOKUP ───────────────────────────────────────────

const DATASETS = {
  [SAMPLE.sentence]: SAMPLE,
  [PASSIVE.sentence]: PASSIVE,
  [COMPOUND_COMPLEX.sentence]: COMPOUND_COMPLEX,
  [APPOSITIVE.sentence]: APPOSITIVE,
};

const EXAMPLES = [
  { label: "Complex",          text: SAMPLE.sentence,           defaultLayer: "clauses" },
  { label: "Passive voice",    text: PASSIVE.sentence,          defaultLayer: "subject-verb" },
  { label: "Compound-complex", text: COMPOUND_COMPLEX.sentence, defaultLayer: "clauses" },
  { label: "Appositive",       text: APPOSITIVE.sentence,       defaultLayer: "appositives" },
];

// ─── COLLAPSE BADGE STYLES ───────────────────────────────────

const COLLAPSE_STYLES = {
  appositive: {
    open: "[",
    close: "]",
    color: "#f59e0b",
    bg: "#f59e0b10",
    border: "#f59e0b30",
    tooltip: "Appositive — click to expand",
  },
  relative: {
    open: "⟨",
    close: "⟩",
    color: "#a855f7",
    bg: "#a855f710",
    border: "#a855f730",
    tooltip: "Relative clause — click to expand",
  },
  dependent: {
    open: "⟨",
    close: "⟩",
    color: "#f59e0b",
    bg: "#f59e0b10",
    border: "#f59e0b30",
    tooltip: "Dependent clause — click to expand",
  },
};

// ─── MEASUREMENT HOOK ─────────────────────────────────────────
// Simplified for single-line: no line detection needed.

function useMeasuredLayout(containerEl, deps) {
  const tokenEls = useRef({});
  const [layout, setLayout] = useState(null);

  const measure = useCallback(() => {
    if (!containerEl) return;
    const cRect = containerEl.getBoundingClientRect();

    const rects = {};
    for (const [key, el] of Object.entries(tokenEls.current)) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      rects[key] = {
        left:   r.left - cRect.left,
        top:    r.top - cRect.top,
        width:  r.width,
        height: r.height,
        right:  r.left - cRect.left + r.width,
        bottom: r.top - cRect.top + r.height,
        cx:     r.left - cRect.left + r.width / 2,
        cy:     r.top - cRect.top + r.height / 2,
      };
    }

    // Find the text baseline (all tokens on one line)
    let textTop = Infinity, textBottom = 0;
    for (const r of Object.values(rects)) {
      textTop = Math.min(textTop, r.top);
      textBottom = Math.max(textBottom, r.bottom);
    }

    // Total content width
    let contentWidth = 0;
    for (const r of Object.values(rects)) {
      contentWidth = Math.max(contentWidth, r.right);
    }

    setLayout({ rects, textTop, textBottom, contentWidth: contentWidth + 40 });
  }, [containerEl]);

  useEffect(() => {
    measure();
    if (!containerEl) return;
    const ro = new ResizeObserver(() => requestAnimationFrame(measure));
    ro.observe(containerEl);
    return () => ro.disconnect();
  }, [measure, containerEl]);

  // Re-measure when dependencies change
  useEffect(() => {
    requestAnimationFrame(measure);
  }, [deps, measure]);

  return { tokenEls, layout, measure };
}

// ─── ANNOTATION OVERLAY ──────────────────────────────────────
// Single-line SVG — no cross-line logic at all.

function AnnotationOverlay({ layout, annotations, selectedId, onSelect }) {
  if (!layout) return null;
  const { rects, textTop, textBottom, contentWidth } = layout;

  const spanRect = (start, end) => {
    let left = Infinity, right = -Infinity;
    for (let i = start; i <= end; i++) {
      const r = rects[i] || rects[`badge-within-${i}`];
      if (!r) continue;
      left = Math.min(left, r.left);
      right = Math.max(right, r.right);
    }
    return left < Infinity ? { left: left - 2, right: right + 2, cx: (left + right) / 2, width: right - left + 4 } : null;
  };

  const centerX = (start, end) => {
    const first = rects[start];
    const last = rects[end || start];
    if (!first || !last) return 0;
    return (first.left + last.right) / 2;
  };

  const arrows = annotations.filter((a) => a.type === "arrow");
  const underlines = annotations.filter((a) => a.type === "underline");
  const braces = annotations.filter((a) => a.type === "brace");

  const elements = [];
  const ABOVE_ZONE = textTop;
  const BELOW_START = textBottom + 6;

  // ── ARROWS (above text) ──
  arrows.forEach((a) => {
    const srcCx = centerX(a.start, a.end);
    let tgtCx;
    if (a.targetSpan) {
      tgtCx = centerX(a.targetSpan[0], a.targetSpan[1]);
    } else {
      tgtCx = centerX(a.target, a.target);
    }

    if (!srcCx || !tgtCx) return;

    const y = ABOVE_ZONE - 4;
    const dist = Math.abs(tgtCx - srcCx);
    const arcH = Math.min(dist * 0.3, 44);
    const midX = (srcCx + tgtCx) / 2;
    const cy = y - arcH;

    const d = `M ${srcCx} ${y} Q ${midX} ${cy} ${tgtCx} ${y}`;
    const angle = Math.atan2(y - cy, tgtCx - midX);
    const aL = 6;
    const isSelected = selectedId === a.id;

    elements.push(
      <g key={a.id} style={{ cursor: "pointer", pointerEvents: "all" }}
        onClick={() => onSelect(isSelected ? null : a)}>
        <path d={d} fill="none" stroke={a.color} strokeWidth={isSelected ? 2.5 : 1.5}
          opacity={isSelected ? 1 : 0.8} />
        <polygon
          points={`${tgtCx},${y} ${tgtCx - aL * Math.cos(angle - 0.4)},${y - aL * Math.sin(angle - 0.4)} ${tgtCx - aL * Math.cos(angle + 0.4)},${y - aL * Math.sin(angle + 0.4)}`}
          fill={a.color}
        />
        {a.label && (
          <text x={midX} y={cy - 5} textAnchor="middle" fill={a.color}
            fontSize="9.5" fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" fontWeight="600"
            opacity={isSelected ? 1 : 0.7}>
            {a.label}
          </text>
        )}
      </g>
    );
  });

  // ── UNDERLINES (below text) ──
  const UL_GAP = 4;

  underlines.forEach((a, ui) => {
    const span = spanRect(a.start, a.end);
    if (!span) return;

    const y = BELOW_START + UL_GAP + ui * 5;
    const isDashed = a.label?.includes("relative") || a.label?.includes("dependent") || a.label?.includes("subordinate") || a.label?.includes("concessive");
    const isSelected = selectedId === a.id;

    elements.push(
      <g key={a.id} style={{ cursor: "pointer", pointerEvents: "all" }}
        onClick={() => onSelect(isSelected ? null : a)}>
        <line x1={span.left} y1={y} x2={span.right} y2={y}
          stroke={a.color} strokeWidth={isSelected ? 4 : 3} strokeLinecap="round"
          strokeDasharray={isDashed ? "7 5" : "none"}
          opacity={isSelected ? 1 : 0.7}
        />
        <text x={span.left + 2} y={y + 14}
          fill={a.color} fontSize="10" fontWeight="600" opacity={isSelected ? 0.9 : 0.6}
          fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace">
          {a.label}
        </text>
      </g>
    );
  });

  // ── BRACES (below underlines) ──
  const braceBaseY = BELOW_START + UL_GAP + underlines.length * 5 + (underlines.length > 0 ? 22 : 0);
  const BRACE_TIP = 16;
  const BRACE_LABEL_GAP = 14;

  braces.forEach((a, bi) => {
    const span = spanRect(a.start, a.end);
    if (!span) return;

    const y = braceBaseY + bi * (BRACE_TIP + BRACE_LABEL_GAP + 6);
    const mid = span.cx;
    const tipY = y + BRACE_TIP;
    const isSelected = selectedId === a.id;

    const dLeft = `M ${span.left} ${y} C ${span.left} ${y + 8}, ${mid - 20} ${tipY}, ${mid} ${tipY}`;
    const dRight = `M ${mid} ${tipY} C ${mid + 20} ${tipY}, ${span.right} ${y + 8}, ${span.right} ${y}`;

    elements.push(
      <g key={a.id} style={{ cursor: "pointer", pointerEvents: "all" }}
        onClick={() => onSelect(isSelected ? null : a)}>
        <path d={dLeft} fill="none" stroke={a.color} strokeWidth={isSelected ? 2.5 : 2}
          strokeLinecap="round" opacity={isSelected ? 1 : 0.7} />
        <path d={dRight} fill="none" stroke={a.color} strokeWidth={isSelected ? 2.5 : 2}
          strokeLinecap="round" opacity={isSelected ? 1 : 0.7} />
        <circle cx={mid} cy={tipY} r="2" fill={a.color} opacity={isSelected ? 1 : 0.7} />
        <text x={mid} y={tipY + BRACE_LABEL_GAP}
          textAnchor="middle" fill={a.color} fontSize="10" fontWeight="600"
          fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" opacity={isSelected ? 0.9 : 0.65}>
          {a.label}
        </text>
      </g>
    );
  });

  // Calculate total SVG height
  const maxY = braces.length > 0
    ? braceBaseY + braces.length * (BRACE_TIP + BRACE_LABEL_GAP + 6) + 10
    : BELOW_START + UL_GAP + underlines.length * 5 + (underlines.length > 0 ? 24 : 10);

  return (
    <svg style={{
      position: "absolute", top: 0, left: 0,
      width: contentWidth, height: maxY,
      pointerEvents: "none", overflow: "visible",
    }}>
      {elements}
    </svg>
  );
}

// ─── FLOATING COLLAPSE TOOLTIP (follows cursor) ─────────────

function FloatingCollapseTooltip({ hoveredRegion, hoverPos, regions }) {
  if (!hoveredRegion || !hoverPos) return null;
  const region = regions.find(r => r.id === hoveredRegion);
  if (!region) return null;
  const style = COLLAPSE_STYLES[region.type] || COLLAPSE_STYLES.appositive;

  return (
    <div
      style={{
        position: "absolute",
        left: hoverPos.x - 14,
        top: hoverPos.y - 28,
        width: "28px",
        height: "18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: style.bg,
        border: `1px solid ${style.border}`,
        borderRadius: "9px",
        zIndex: 10,
        pointerEvents: "none",
        transition: "left 0.05s, top 0.05s",
        padding: 0,
      }}
    >
      <span style={{
        fontSize: "11px",
        fontWeight: 700,
        color: style.color,
        lineHeight: 1,
        letterSpacing: "2px",
      }}>
        ···
      </span>
    </div>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────

export default function SentenceStudio({ data: initialData = SAMPLE }) {
  const { dark } = useTheme();
  const c = getThemeColors(dark);
  const [inputText, setInputText] = useState(initialData.sentence);
  const [analysisData, setAnalysisData] = useState(initialData);
  const [analyzing, setAnalyzing] = useState(false);
  const [active, setActive] = useState(["clauses"]);
  const [selected, setSelected] = useState(null);
  const [collapsed, setCollapsed] = useState(new Set());
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const [hoverPos, setHoverPos] = useState(null);
  const [containerEl, setContainerEl] = useState(null);
  const inputRef = useRef(null);

  const containerRef = useCallback((node) => { if (node) setContainerEl(node); }, []);

  const data = analysisData;

  // ── Collapse logic ──
  const hiddenTokenIds = useMemo(() => {
    const hidden = new Set();
    (data.collapsible || []).forEach((region) => {
      if (collapsed.has(region.id)) {
        for (let i = region.start; i <= region.end; i++) hidden.add(i);
      }
    });
    return hidden;
  }, [data.collapsible, collapsed]);

  const toggleCollapse = (regionId) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(regionId)) next.delete(regionId);
      else next.add(regionId);
      return next;
    });
    setSelected(null);
  };

  // ── Analysis ──
  const handleAnalyze = async () => {
    if (!inputText.trim()) return;
    const dataset = DATASETS[inputText.trim()];
    if (dataset) {
      setAnalysisData(dataset);
      setActive(["clauses"]);
      setSelected(null);
      setCollapsed(new Set());
      return;
    }
    setAnalyzing(true);
    setSelected(null);
    setCollapsed(new Set());
    await new Promise((r) => setTimeout(r, 800));
    const words = inputText.trim().match(/[\w'']+|[^\s\w]/g) || [];
    const tokens = words.map((w, i) => ({
      id: i, text: w, pos: /^[.,;:!?'"()\-—]$/.test(w) ? "punctuation" : "word",
    }));
    setAnalysisData({ sentence: inputText.trim(), tokens, annotations: [], collapsible: [] });
    setActive([]);
    setAnalyzing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAnalyze(); }
  };

  // ── Layer toggle (single-layer) ──
  const toggle = (layer) => {
    setActive((p) => p.includes(layer) ? [] : [layer]);
    setSelected(null);
  };

  // ── Filtered annotations ──
  const activeAnns = useMemo(() => {
    const layerAnns = data.annotations.filter((a) => active.includes(a.layer));
    // Hide annotations that reference hidden tokens
    return layerAnns.filter((a) => {
      // Check start/end span
      for (let i = a.start; i <= a.end; i++) {
        if (hiddenTokenIds.has(i)) return false;
      }
      // Check arrow targets
      if (a.target !== undefined && hiddenTokenIds.has(a.target)) return false;
      if (a.targetSpan) {
        for (let i = a.targetSpan[0]; i <= a.targetSpan[1]; i++) {
          if (hiddenTokenIds.has(i)) return false;
        }
      }
      return true;
    });
  }, [data.annotations, active, hiddenTokenIds]);

  // ── Measurement ──
  const depKey = `${active.join(",")}-${[...collapsed].join(",")}-${data.sentence}`;
  const { tokenEls, layout } = useMeasuredLayout(containerEl, depKey);

  // ── POS highlight map ──
  const hlMap = useMemo(() => {
    const m = {};
    activeAnns.forEach((a) => {
      if (a.type === "highlight") for (let i = a.start; i <= a.end; i++) m[i] = a;
    });
    return m;
  }, [activeAnns]);

  const nonHlAnns = activeAnns.filter((a) => a.type !== "highlight");

  // ── Build visible items (tokens + collapse badges) ──
  const visibleItems = useMemo(() => {
    const items = [];
    const regions = data.collapsible || [];
    let i = 0;
    while (i < data.tokens.length) {
      // Check if this token starts a collapsed region
      const colRegion = regions.find((r) => collapsed.has(r.id) && r.start === i);
      if (colRegion) {
        // Count how many collapsed regions fully contain this one (nesting depth)
        const depth = regions.filter(r =>
          collapsed.has(r.id) && r.start <= colRegion.start && r.end >= colRegion.end
        ).length;
        items.push({ type: "badge", region: colRegion, depth });
        i = colRegion.end + 1;
        continue;
      }
      // Skip hidden tokens (part of a collapsed region but not the start)
      if (hiddenTokenIds.has(i)) {
        i++;
        continue;
      }
      items.push({ type: "token", token: data.tokens[i] });
      i++;
    }
    return items;
  }, [data.tokens, data.collapsible, collapsed, hiddenTokenIds]);

  // ── Which regions are expanded and visible? ──
  const expandedRegions = useMemo(() => {
    return (data.collapsible || []).filter((r) => !collapsed.has(r.id));
  }, [data.collapsible, collapsed]);

  // Map token IDs → collapsible region ID (innermost/smallest region wins)
  const tokenToRegion = useMemo(() => {
    const m = {};
    // Sort by span size descending — largest first, so smallest overwrites
    const sorted = [...expandedRegions].sort((a, b) => (b.end - b.start) - (a.end - a.start));
    for (const r of sorted) {
      for (let i = r.start; i <= r.end; i++) m[i] = r.id;
    }
    return m;
  }, [expandedRegions]);

  const MONO = "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace";

  return (
    <div style={{
      height: "100%", background: c.bg, color: c.textBody,
      display: "flex", flexDirection: "column", overflow: "auto",
      padding: "20px 28px",
    }}>
      {/* ── Sentence Input ── */}
      <div style={{
        marginBottom: "16px", padding: "16px 20px",
        background: c.cardBg, borderRadius: "8px", border: `1px solid ${c.border}`,
        boxShadow: c.cardShadow,
      }}>
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <label style={{
              display: "block", fontSize: "9px", fontWeight: 700,
              letterSpacing: "0.12em", textTransform: "uppercase",
              color: c.textSubhead, marginBottom: "6px", fontFamily: MONO,
            }}>Sentence</label>
            <textarea
              ref={inputRef}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type or paste a sentence to analyze…"
              rows={2}
              style={{
                width: "100%", padding: "10px 14px", fontSize: "15px",
                background: c.inputBg, border: `1px solid ${c.inputBorder}`,
                borderRadius: "6px", color: c.textTextarea, resize: "vertical",
                outline: "none", lineHeight: 1.5, transition: "border-color 0.15s",
              }}
              onFocus={(e) => e.target.style.borderColor = c.inputFocusBorder}
              onBlur={(e) => e.target.style.borderColor = c.inputBorder}
            />
          </div>
          <button onClick={handleAnalyze} disabled={analyzing || !inputText.trim()}
            style={{
              padding: "10px 20px", fontSize: "12px", fontWeight: 600,
              background: analyzing ? c.disabledBtnBg : c.activeBtnBg,
              color: analyzing ? c.disabledText : c.accent,
              border: `1px solid ${c.buttonBorder}`, borderRadius: "6px",
              cursor: analyzing ? "wait" : "pointer",
              whiteSpace: "nowrap", transition: "all 0.12s",
              alignSelf: "flex-end", flexShrink: 0,
            }}>
            {analyzing ? "Analyzing…" : "Analyze →"}
          </button>
        </div>

        {/* Preset examples */}
        <div style={{ display: "flex", gap: "6px", marginTop: "10px", flexWrap: "wrap" }}>
          <span style={{
            fontSize: "9px", color: c.textDim, fontFamily: MONO,
            alignSelf: "center", marginRight: "2px",
          }}>Try:</span>
          {EXAMPLES.map((ex) => (
            <button key={ex.label}
              onClick={() => {
                setInputText(ex.text);
                const dataset = DATASETS[ex.text];
                if (dataset) {
                  setAnalysisData(dataset);
                  setActive([ex.defaultLayer || "clauses"]);
                  setSelected(null);
                  setCollapsed(new Set());
                }
              }}
              style={{
                padding: "3px 8px", fontSize: "10px", fontFamily: MONO,
                background: inputText === ex.text ? c.activePresetBg : "transparent",
                color: inputText === ex.text ? c.accent : c.textMuted5,
                border: `1px solid ${inputText === ex.text ? c.presetActiveBorder : c.presetBorder}`,
                borderRadius: "4px", cursor: "pointer", transition: "all 0.1s",
              }}>
              {ex.label}
            </button>
          ))}
        </div>

        {/* Hint for non-preset sentences */}
        {data.annotations.length === 0 && data.tokens.length > 0 && !analyzing && (
          <p style={{
            marginTop: "8px", fontSize: "10.5px", color: c.textMuted4,
            fontFamily: MONO, fontStyle: "italic",
          }}>
            ⓘ In production, "Analyze" sends this to Claude for grammatical parsing. Preset sentences have full annotations.
          </p>
        )}
      </div>

      {/* ── Layers bar ── */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: "6px",
        marginBottom: "12px", alignItems: "center",
      }}>
        <span style={{
          fontSize: "9px", fontWeight: 700, letterSpacing: "0.12em",
          textTransform: "uppercase", color: c.textSubhead, fontFamily: MONO,
          marginRight: "4px",
        }}>Layers</span>
        {Object.entries(LAYERS).map(([key, cfg]) => {
          const on = active.includes(key);
          return (
            <button key={key} onClick={() => toggle(key)} style={{
              display: "inline-flex", alignItems: "center", gap: "5px",
              padding: "4px 10px", borderRadius: "14px",
              border: on ? `1px solid ${c.iconBadgeBorderOn}` : `1px solid ${c.border}`,
              background: on ? c.activeToggleBg : "transparent",
              cursor: "pointer", transition: "all 0.1s",
            }}>
              <span style={{
                fontSize: "10px", fontWeight: 700, fontFamily: MONO,
                color: on ? c.accent : c.textSubhead,
              }}>
                {cfg.icon}
              </span>
              <span style={{
                fontSize: "11px", fontWeight: on ? 600 : 400,
                color: on ? c.textToken : c.textMuted4, fontFamily: MONO,
              }}>{cfg.name}</span>
            </button>
          );
        })}

        {/* Structure toggles inline */}
        {(data.collapsible || []).length > 0 && (
          <>
            <span style={{
              fontSize: "9px", fontWeight: 700, letterSpacing: "0.12em",
              textTransform: "uppercase", color: c.textSubhead, fontFamily: MONO,
              marginLeft: "12px", marginRight: "4px",
            }}>Structure</span>
            {(data.collapsible || []).map((region) => {
              const isCollapsed = collapsed.has(region.id);
              const st = COLLAPSE_STYLES[region.type] || COLLAPSE_STYLES.appositive;
              return (
                <button
                  key={region.id}
                  onClick={() => toggleCollapse(region.id)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "4px",
                    padding: "4px 10px", borderRadius: "14px",
                    border: `1px solid ${isCollapsed ? st.border : c.border}`,
                    background: isCollapsed ? `${st.color}08` : "transparent",
                    cursor: "pointer", transition: "all 0.1s",
                  }}
                >
                  <span style={{
                    fontSize: "10px", fontWeight: 600, fontFamily: MONO,
                    color: st.color,
                  }}>
                    {isCollapsed ? "+" : "−"}
                  </span>
                  <span style={{
                    fontSize: "10.5px", fontWeight: isCollapsed ? 400 : 500,
                    color: isCollapsed ? c.textMuted4 : st.color, fontFamily: MONO,
                  }}>{region.label}</span>
                </button>
              );
            })}
          </>
        )}
      </div>

      {/* ── Sentence Display ── */}
      {data.tokens.length > 0 && (
        <div style={{
          overflowX: "auto",
          background: c.cardBg,
          borderRadius: "8px",
          border: `1px solid ${c.border}`,
          paddingBottom: "8px",
          boxShadow: c.cardShadow,
        }}>
          {/* Scrollable inner container */}
          <div
            ref={containerRef}
            style={{
              position: "relative",
              whiteSpace: "nowrap",
              padding: "112px 32px 160px",
              minWidth: "fit-content",
            }}
          >
            {/* Floating collapse tooltip (follows cursor) */}
            <FloatingCollapseTooltip
              hoveredRegion={hoveredRegion}
              hoverPos={hoverPos}
              regions={expandedRegions}
            />

            {/* Tokens + collapse badges — single line */}
            <div>
              {visibleItems.map((item) => {
                if (item.type === "badge") {
                  const region = item.region;
                  const style = COLLAPSE_STYLES[region.type] || COLLAPSE_STYLES.appositive;
                  return (
                    <span
                      key={`badge-${region.id}`}
                      ref={(el) => (tokenEls.current[`badge-${region.id}`] = el)}
                      onClick={() => toggleCollapse(region.id)}
                      title={`${region.label}: ${region.summary}\nClick to expand`}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        verticalAlign: "top",
                        padding: "2px 8px",
                        marginLeft: "4px",
                        marginRight: "4px",
                        cursor: "pointer",
                        borderRadius: "4px",
                        background: style.bg,
                        border: `1px solid ${style.border}`,
                        transition: "all 0.15s",
                        height: "32px",
                      }}
                    >
                      <span style={{
                        fontSize: "15px", fontFamily: MONO,
                        fontWeight: 600, color: style.color,
                        lineHeight: 1, letterSpacing: "1px",
                      }}>
                        {style.open.repeat(item.depth)}…{style.close.repeat(item.depth)}
                      </span>
                    </span>
                  );
                }

                const tok = item.token;
                const hl = hlMap[tok.id];
                const isPunct = tok.pos === "punctuation";
                const isInSel = selected && tok.id >= selected.start && tok.id <= selected.end;
                const regionId = tokenToRegion[tok.id];

                return (
                  <span
                    key={tok.id}
                    ref={(el) => (tokenEls.current[tok.id] = el)}
                    onMouseEnter={regionId ? () => setHoveredRegion(regionId) : undefined}
                    onMouseLeave={regionId ? () => { setHoveredRegion(null); setHoverPos(null); } : undefined}
                    onMouseMove={regionId ? (e) => {
                      if (!containerEl) return;
                      const cr = containerEl.getBoundingClientRect();
                      setHoverPos({ x: e.clientX - cr.left, y: e.clientY - cr.top });
                    } : undefined}
                    onClick={() => {
                      if (regionId) {
                        toggleCollapse(regionId);
                        return;
                      }
                      const ann = activeAnns.find(
                        (a) => tok.id >= a.start && tok.id <= a.end && a.type !== "highlight"
                      );
                      setSelected(ann || null);
                    }}
                    style={{
                      display: "inline-flex",
                      flexDirection: "column",
                      alignItems: "center",
                      verticalAlign: "top",
                      padding: isPunct ? "0 0" : "2px 5px",
                      marginLeft: isPunct ? "-2px" : "5px",
                      marginRight: isPunct ? "3px" : "0",
                      cursor: "pointer",
                      borderRadius: "3px",
                      background: hl ? `${hl.color}12` : isInSel ? c.tokenHoverBg : "transparent",
                      transition: "background 0.1s",
                    }}
                  >
                    <span style={{
                      fontSize: "22px", fontWeight: 400,
                      color: hl ? hl.color : isInSel ? c.textTokenSelected : c.textToken,
                      lineHeight: 1, whiteSpace: "nowrap",
                    }}>
                      {tok.text}
                    </span>
                    {hl && (
                      <span style={{
                        fontSize: "8.5px", fontWeight: 700, marginTop: "2px",
                        fontFamily: MONO,
                        color: hl.color, opacity: 0.6, letterSpacing: "0.03em",
                        lineHeight: 1,
                      }}>
                        {hl.label}
                      </span>
                    )}
                  </span>
                );
              })}
            </div>

            {/* SVG annotation overlay */}
            <AnnotationOverlay
              layout={layout}
              annotations={nonHlAnns}
              selectedId={selected?.id}
              onSelect={setSelected}
            />
          </div>

          {/* Scroll hint */}
          <div style={{
            textAlign: "center", padding: "4px 0 2px",
            fontSize: "9px", color: c.textScrollHint, fontFamily: MONO,
          }}>
            ← scroll →
          </div>
        </div>
      )}

      {/* ── Below sentence: annotations, key, detail ── */}
      {nonHlAnns.filter(a => a.description).length > 0 && (
        <div style={{ marginTop: "16px" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
            {nonHlAnns.filter(a => a.description).map((a) => (
              <button key={a.id}
                onClick={() => setSelected(selected?.id === a.id ? null : a)}
                style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  padding: "5px 9px", borderRadius: "4px",
                  border: selected?.id === a.id ? `1px solid ${a.color}30` : `1px solid ${c.border}`,
                  background: selected?.id === a.id ? `${a.color}08` : c.cardBg,
                  cursor: "pointer", transition: "all 0.1s",
                }}>
                <span style={{
                  width: "6px", height: "6px", borderRadius: "50%",
                  background: a.color, flexShrink: 0,
                }} />
                <span style={{
                  fontSize: "10px", color: c.textMuted2, fontFamily: MONO,
                }}>{a.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Key + Detail row */}
      <div style={{ display: "flex", gap: "16px", marginTop: "12px", flexWrap: "wrap" }}>
        {/* Visual key */}
        <div style={{
          display: "flex", gap: "12px", alignItems: "baseline", flexWrap: "wrap",
        }}>
          {[
            ["↷", "Arrows"],
            ["━", "Underlines"],
            ["╰╮╯", "Braces"],
            ["▮", "Highlights"],
            ["[…]", "Collapsed"],
          ].map(([s, n]) => (
            <span key={n} style={{ display: "inline-flex", gap: "4px", alignItems: "baseline" }}>
              <span style={{ fontSize: "9px", color: c.textMuted4, fontFamily: MONO }}>{s}</span>
              <span style={{ fontSize: "9px", color: c.textMuted3, fontFamily: MONO }}>{n}</span>
            </span>
          ))}
        </div>

        {/* Selected annotation detail */}
        {selected && (
          <div style={{
            flex: 1, minWidth: "200px", padding: "10px 14px", borderRadius: "6px",
            background: c.cardBg, border: `1px solid ${selected.color}20`,
            boxShadow: c.cardShadow,
          }}>
            <p style={{
              fontSize: "12px", fontWeight: 600, color: selected.color,
              marginBottom: "4px", fontFamily: MONO,
            }}>{selected.label}</p>
            <p style={{ fontSize: "12px", lineHeight: 1.55, color: c.textMuted1 }}>
              {selected.description}
            </p>
            <p style={{
              fontSize: "10px", color: c.textDim, marginTop: "4px",
              fontFamily: MONO, fontStyle: "italic",
            }}>
              "{data.tokens.slice(selected.start, selected.end + 1).map(t => t.text).join(" ")}"
            </p>
          </div>
        )}
      </div>

      {active.length === 0 && data.annotations.length > 0 && (
        <div style={{ marginTop: "32px", textAlign: "center", padding: "28px", color: c.textEmptyState }}>
          <p style={{ fontSize: "12px", fontFamily: MONO }}>
            Select a layer above
          </p>
        </div>
      )}
    </div>
  );
}

